<template>
  <div class="markdown-editor">
    <div v-if="editing">
      <v-textarea
          v-show="!showEditor"
          :rows="lineCount"
          :label="label"
          :value="value"
          @input="update"
          auto-grow
          :error-messages="errors"
          @click="onShowEditor"
          @focus="onShowEditor"
      ></v-textarea>
      <markdown-editor
          v-show="showEditor"
          ref="textarea"
          height="auto"
          autofocus
          :value="value"
          @input="update"
          :toolbar="toolbar"
      ></markdown-editor>
    </div>

    <div v-else @click="click">
      <vue-markdown
        class="markdown mark_down__wrapper"
        :source="value"
      ></vue-markdown>
    </div>
  </div>
</template>

<script>
  import markdown from 'vue-markdown'

  export default {
    name: 'Markdown',
    props: {
      label: {
        type: String,
        default: ""
      },
      fieldType: {
        type: String,
        default: "textarea"
      },
      errors: {
        type: Array,
        default: () => { return [] }
      },
      value: String,
      editing: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        toolbar: 'clean redo undo | bold italic strikethrough heading | image link | numlist bullist quote | preview fullscreen',
        showEditor: this.$props.fieldType !== 'input',
      }
    },
    computed: {
      lineCount() {
        if (this.fieldType === 'input') {
          return this.value.length ? Math.max(this.value.split(/\r\n|\r|\n/).length, 1) : 1;
        }
        return this.value.length ? Math.max(this.value.split(/\r\n|\r|\n/).length, 5) : 5;
      }
    },
    methods: {
      update(value) {
        this.$emit('input', value)
      },
      click() {
        this.$emit('click')
      },
      focus() {
        setTimeout(() => {
          this.$refs.textarea.focus()
        }, 300)
      },
      insertAtCursor(value) {
        if (this.$refs.textarea.$el.classList.contains('v-md-container')) {
          this.$refs.textarea.editor.replaceSelection(value);
        }
      },
      focusEditor() {
        this.$refs.textarea.editor.focus()
      },
      onShowEditor() {
        this.showEditor = true
      },
      hideEditor() {
        this.showEditor = false
      },
      pasteMarkdown(e) {
        let items = e.clipboardData.items

        if (items) {
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf("image") !== -1) {
              this.$api.files.save(items[i].getAsFile()).then(({data}) => {
                let file = data.file;

                this.insertAtCursor("![" + file.name + "](" + this.$baseURL + file.url + ")")

                this.$emit('paste', file)
              })
            }
          }
        }
      },
      initPaste() {
        if (this.$refs.textarea.$el && this.$refs.textarea.$el.classList.contains('v-md-container')) {
          this.$refs.textarea.$el.getElementsByClassName('CodeMirror-wrap')[0].addEventListener("paste", this.pasteMarkdown, false);
        }
      },
      removePaste() {
        if (this.$refs.textarea.$el && this.$refs.textarea.$el.classList.contains('v-md-container')) {
          this.$refs.textarea.$el.getElementsByClassName('CodeMirror-wrap')[0].removeEventListener("paste", this.pasteMarkdown, false);
        }
      }
    },
    watch: {
      editing() {
        if (this.editing) {
          this.$nextTick(() => {
            this.initPaste()
          })
        } else {
          this.removePaste()
        }
      }
    },
    mounted() {
      if (this.editing) {
        this.initPaste()
      }
    },
    components: {
      'vue-markdown': markdown,
    },
  }
</script>

<style lang="scss">
.markdown-editor {
  .mr-5 {
    margin-right: .315rem !important;
  }
}

.theme--dark .markdown-editor {

  caret-color: white !important;

  .v-md-container {
    border-color: #626161;
  }

  .v-md-container>.v-md-toolbar {
    background-color: #3f3f3f;
    border-bottom: 1px solid #626161;
  }

  .v-md-preview {
    background-color: #3f3f3f;
    color: white;

    pre {
      background-color: #2B2B2B;
      border: 1px solid #626161;
      padding: 8px 10px;

      code {
        background: transparent;
        padding: 0;
        box-shadow: none;
        border: none;
      }
    }

    code {
      border: 1px solid #626161;
      background-color: #2B2B2B;
    }

    blockquote {
      border-left: 4px solid #dddddd;
      padding: 0 15px;
      color: #777777;
    }
  }

  .CodeMirror {

    .CodeMirror-cursor {
      border-left: 1px solid white;
      background: white;
    }

    background-color: #3f3f3f;
    color: white;

    .CodeMirror-line {
      text-shadow: 1px 1px 2px #3f3f3f;
    }

    .cm-keyword {
      color: #ec6dff;
    }

    .cm-comment {
      color: #ff8b8b;
    }

    .cm-header {
      color: #c1f1d0;
    }

    .cm-link {
      color: #5cc1a6;
    }

    .cm-variable-2 {
      color: #afbce1;
    }

    .cm-string {
      color: #fffc88;
    }
  }
}

.mark_down__wrapper {
  a {
    color: #4183C4;
    text-decoration: none;
  }

  a.absent {
    color: #cc0000;
  }

  a.anchor {
    display: block;
    padding-left: 30px;
    margin-left: -30px;
    cursor: pointer;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 20px 0 10px;
    padding: 0;
    font-weight: bold;
    -webkit-font-smoothing: antialiased;
    cursor: text;
    position: relative;
  }

  h2:first-child, h1:first-child, h1:first-child + h2, h3:first-child, h4:first-child, h5:first-child, h6:first-child {
    margin-top: 0;
    padding-top: 0;
  }

  h1:hover a.anchor, h2:hover a.anchor, h3:hover a.anchor, h4:hover a.anchor, h5:hover a.anchor, h6:hover a.anchor {
    text-decoration: none;
  }

  code {
    box-shadow: none;
  }

  h1 tt, h1 code {
    font-size: inherit;
  }

  h2 tt, h2 code {
    font-size: inherit;
  }

  h3 tt, h3 code {
    font-size: inherit;
  }

  h4 tt, h4 code {
    font-size: inherit;
  }

  h5 tt, h5 code {
    font-size: inherit;
  }

  h6 tt, h6 code {
    font-size: inherit;
  }

  h1 {
    font-size: 28px;
    color: black;
  }

  h2 {
    font-size: 24px;
    border-bottom: 1px solid #cccccc;
    color: black;
  }

  h3 {
    font-size: 18px;
  }

  h4 {
    font-size: 16px;
  }

  h5 {
    font-size: 14px;
  }

  h6 {
    color: #777777;
    font-size: 14px;
  }

  p, blockquote, ul, ol, dl, li, table, pre {
    margin: 15px 0;
  }

  hr {
    border: 0 none;
    color: #cccccc;
    height: 4px;
    padding: 0;
  }

  body > h2:first-child {
    margin-top: 0;
    padding-top: 0;
  }

  body > h1:first-child {
    margin-top: 0;
    padding-top: 0;
  }

  body > h1:first-child + h2 {
    margin-top: 0;
    padding-top: 0;
  }

  body > h3:first-child, body > h4:first-child, body > h5:first-child, body > h6:first-child {
    margin-top: 0;
    padding-top: 0;
  }

  a:first-child h1, a:first-child h2, a:first-child h3, a:first-child h4, a:first-child h5, a:first-child h6 {
    margin-top: 0;
    padding-top: 0;
  }

  h1 p, h2 p, h3 p, h4 p, h5 p, h6 p {
    margin-top: 0;
  }

  li p.first {
    display: inline-block;
  }

  ul, ol {
    padding-left: 30px;
  }

  ul :first-child, ol :first-child {
    margin-top: 0;
  }

  ul :last-child, ol :last-child {
    margin-bottom: 0;
  }

  dl {
    padding: 0;
  }

  dl dt {
    font-size: 14px;
    font-weight: bold;
    font-style: italic;
    padding: 0;
    margin: 15px 0 5px;
  }

  dl dt:first-child {
    padding: 0;
  }

  dl dt > :first-child {
    margin-top: 0;
  }

  dl dt > :last-child {
    margin-bottom: 0;
  }

  dl dd {
    margin: 0 0 15px;
    padding: 0 15px;
  }

  dl dd > :first-child {
    margin-top: 0;
  }

  dl dd > :last-child {
    margin-bottom: 0;
  }

  blockquote {
    border-left: 4px solid #dddddd;
    padding: 0 15px;
    color: #777777;
  }

  blockquote > :first-child {
    margin-top: 0;
  }

  blockquote > :last-child {
    margin-bottom: 0;
  }

  table {
    padding: 0;
  }

  table tr {
    border-top: 1px solid #cccccc;
    background-color: white;
    margin: 0;
    padding: 0;
  }

  table tr:nth-child(2n) {
    background-color: #f8f8f8;
  }

  table tr th {
    font-weight: bold;
    border: 1px solid #cccccc;
    text-align: left;
    margin: 0;
    padding: 6px 13px;
  }

  table tr td {
    border: 1px solid #cccccc;
    text-align: left;
    margin: 0;
    padding: 6px 13px;
  }

  table tr th :first-child, table tr td :first-child {
    margin-top: 0;
  }

  table tr th :last-child, table tr td :last-child {
    margin-bottom: 0;
  }

  img {
    max-width: 100%;
  }

  span.frame {
    display: block;
    overflow: hidden;
  }

  span.frame > span {
    border: 1px solid #dddddd;
    display: block;
    float: left;
    overflow: hidden;
    margin: 13px 0 0;
    padding: 7px;
    width: auto;
  }

  span.frame span img {
    display: block;
    float: left;
  }

  span.frame span span {
    clear: both;
    color: #333333;
    display: block;
    padding: 5px 0 0;
  }

  span.align-center {
    display: block;
    overflow: hidden;
    clear: both;
  }

  span.align-center > span {
    display: block;
    overflow: hidden;
    margin: 13px auto 0;
    text-align: center;
  }

  span.align-center span img {
    margin: 0 auto;
    text-align: center;
  }

  span.align-right {
    display: block;
    overflow: hidden;
    clear: both;
  }

  span.align-right > span {
    display: block;
    overflow: hidden;
    margin: 13px 0 0;
    text-align: right;
  }

  span.align-right span img {
    margin: 0;
    text-align: right;
  }

  span.float-left {
    display: block;
    margin-right: 13px;
    overflow: hidden;
    float: left;
  }

  span.float-left span {
    margin: 13px 0 0;
  }

  span.float-right {
    display: block;
    margin-left: 13px;
    overflow: hidden;
    float: right;
  }

  span.float-right > span {
    display: block;
    overflow: hidden;
    margin: 13px auto 0;
    text-align: right;
  }

  code, tt {
    margin: 0 2px;
    padding: 0 5px;
    white-space: nowrap;
    border: 1px solid #eaeaea;
    background-color: #f8f8f8;
    border-radius: 3px;
  }

  pre code {
    margin: 0;
    padding: 0;
    white-space: pre;
    border: none;
    background: transparent;
  }

  .highlight pre {
    background-color: #f8f8f8;
    border: 1px solid #cccccc;
    font-size: 13px;
    line-height: 19px;
    overflow: auto;
    padding: 6px 10px;
    border-radius: 3px;
  }

  pre {
    background-color: #f8f8f8;
    border: 1px solid #cccccc;
    font-size: 13px;
    line-height: 19px;
    overflow: auto;
    padding: 6px 10px;
    border-radius: 3px;
  }

  pre code, pre tt {
    background-color: transparent;
    border: none;
  }
  pre:before, code:before, pre:after, code:after {
    display: none;
  }
}
.theme--dark {
  .mark_down__wrapper {
    code, tt {
      border: 1px solid #626161;
      background-color: #2B2B2B;
    }

    pre code {
      background: transparent;
    }

    .highlight pre {
      background-color: #626161;
      border: 1px solid #626161;
    }

    pre {
      background-color: #2B2B2B;
      border: 1px solid #626161;
    }
    pre code, pre tt {
      background-color: transparent;
      border: none;
      padding: 3px;
    }

    h1 {
      color: #fff;
    }

    h2 {
      border-bottom: 1px solid #999999;
      color: #fff;
    }
  }
}
</style>